import {
    MDXEditor,
    headingsPlugin,
    listsPlugin,
    quotePlugin,
    thematicBreakPlugin,
    markdownShortcutPlugin,
    linkPlugin,
    linkDialogPlugin,
    toolbarPlugin,
    tablePlugin,
    imagePlugin,
    diffSourcePlugin,
    UndoRedo,
    BoldItalicUnderlineToggles,
    BlockTypeSelect,
    CreateLink,
    ListsToggle,
    InsertThematicBreak,
    InsertTable,
    InsertImage,
    type MDXEditorMethods,
} from '@mdxeditor/editor';
import { forwardRef, useState, useRef } from 'react';
import { cn } from '../../../lib/utils';
import { Maximize2, Minimize2 } from 'lucide-react';

import '@mdxeditor/editor/style.css';
import './editor.css';
import ButtonV2 from '../button/v2';

interface CansoEditorProps {
    id: string;
    markdown: string;
    placeholder?: string;
    readOnly?: boolean;
    className?: string;
    toolbarClassName?: string;
    contentEditableClassName?: string;
    showToolbar?: boolean;
    autoHideToolbar?: boolean;
    enableImageUpload?: boolean;
    enableLinkDialog?: boolean;
    allowFullScreen?: boolean;
    onChange?: (markdown: string) => void;
    imageUploadHandler?: (file: File) => Promise<string>;
}

export type { MDXEditorMethods };

const CansoEditor = forwardRef<MDXEditorMethods, CansoEditorProps>(
    ({ 
        id,
        markdown,
        placeholder, 
        readOnly = false, 
        className = '',
        toolbarClassName = '',
        showToolbar = true,
        autoHideToolbar = true,
        contentEditableClassName = '',
        enableImageUpload = false,
        enableLinkDialog = false,
        allowFullScreen = true,
        imageUploadHandler,
        onChange,
    }, ref) => {
        const [isFullScreen, setIsFullScreen] = useState(false);
        const [isFocused, setIsFocused] = useState(false);
        const [isToolbarInteracting, setIsToolbarInteracting] = useState(false);
        const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

        // Toolbar is visible when focused or interacting
        const isToolbarVisible = autoHideToolbar && !isFullScreen ? (isFocused || isToolbarInteracting) : true;

        const toggleFullScreen = () => {
            setIsFullScreen(!isFullScreen);
        };

        const handleEditorFocus = () => {
            setIsFocused(true);
            // clear any pending blur timeout
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = null;
            }
        };

        const handleEditorBlur = (e: React.FocusEvent) => {
            const relatedTarget = e.relatedTarget as HTMLElement;
            const toolbar = (e.currentTarget as HTMLElement).querySelector('.mdxeditor-toolbar');
            
            // if clicked within toolbar or dropdown, don't hide
            if (toolbar && relatedTarget && toolbar.contains(relatedTarget)) {
                return;
            }
            
            // delay to allow toolbar interactions
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
            
            blurTimeoutRef.current = setTimeout(() => {
                setIsFocused(false);
            }, 200);
        };

        const handleToolbarMouseEnter = () => {
            if (autoHideToolbar) {
                setIsToolbarInteracting(true);
            }
        };

        const handleToolbarMouseLeave = () => {
            if (autoHideToolbar) {
                setIsToolbarInteracting(false);
            }
        };

        const handleToolbarMouseDown = () => {
            if (autoHideToolbar) {
                setIsToolbarInteracting(true);
                setTimeout(() => {
                    setIsToolbarInteracting(false);
                }, 300);
            }
        };

        return (
            <div 
                id={id} 
                className={cn(
                    'mdxeditor',
                    className,
                    autoHideToolbar && 'auto-hide-toolbar',
                    autoHideToolbar && isToolbarVisible && 'toolbar-visible',
                    isFullScreen && 'fixed inset-0 z-[10000] bg-white h-[100vh] overflow-auto'
                )}
                onFocusCapture={handleEditorFocus}
                onBlurCapture={handleEditorBlur}
            >
                <MDXEditor
                    ref={ref}
                    markdown={markdown}
                    onChange={onChange}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    className={cn(
                        toolbarClassName, 
                        !showToolbar && 'hide-toolbar',
                        'transition-opacity duration-200'
                    )}
                    contentEditableClassName={cn(
                        'overflow-x-auto h-[500px]',
                        contentEditableClassName,
                        isFullScreen && 'h-[calc(100vh-1px)] pb-12'
                    )}
                    plugins={[
                        diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown: '' }),
                        headingsPlugin(),
                        listsPlugin(),
                        quotePlugin(),
                        thematicBreakPlugin(),
                        linkPlugin(),
                        linkDialogPlugin(),
                        tablePlugin(),
                        markdownShortcutPlugin(),
                        toolbarPlugin({
                            toolbarContents: () => showToolbar && (
                                <div 
                                    className={cn('w-full flex items-center justify-between', isFocused ? 'opacity-100' : 'opacity-50')}
                                    onMouseEnter={handleToolbarMouseEnter}
                                    onMouseLeave={handleToolbarMouseLeave}
                                    onMouseDown={handleToolbarMouseDown}
                                >
                                    <div className='flex divider-x gap-2 divide-gray-300'>
                                        <BlockTypeSelect />
                                        <div className="h-5 border-r border-gray-300 mt-1"></div>
                                        <BoldItalicUnderlineToggles />
                                        <div className="h-5 border-r border-gray-300 mt-1"></div>
                                        <ListsToggle options={['bullet', 'number', 'check']} />
                                        <div className="h-5 border-r border-gray-300 mt-1"></div>
                                        <InsertTable />
                                    </div>
                                    <div className='flex divider-x gap-2 divide-gray-300'>
                                        <div className='flex items-center'>
                                            {enableLinkDialog && <CreateLink />}
                                            {enableImageUpload && <InsertImage />}
                                            <InsertThematicBreak />
                                        </div>
                                        <div className="h-5 border-r border-gray-300 mt-1"/>
                                        <UndoRedo />
                                        {allowFullScreen && (
                                            <>
                                                <div className="h-5 border-r border-gray-300 mt-1"/>
                                                <ButtonV2
                                                    variant="ghost"
                                                    size="icon"
                                                    prefix={isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                                                    className='w-7 h-7 p-1 hover:bg-gray-200 rounded transition-colors'
                                                    title={isFullScreen ? 'Exit Full Screen' : 'Enter Full Screen'}
                                                    aria-label={isFullScreen ? 'Exit Full Screen' : 'Enter Full Screen'}
                                                    onClick={toggleFullScreen}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            ),
                        }),
                    ]}
                />
            </div>
        );
    }
);

CansoEditor.displayName = 'CansoEditor';

export default CansoEditor;